import { Controller } from "@hotwired/stimulus"

// Testimonial carousel on the membership landing page, and the speakers row on
// a Virtual Meeting offering page (TMS-1613). The track is a native
// scroll-snap list, so touch and trackpad swipes need no handling here; the
// arrows, dots, and Left/Right keys page it, and the dots — or the "2 of 5"
// counter — follow whatever slides are in view. Paging wraps from the last
// slide back to the first.
// Snapped positions can sit a few pixels short of the scroll range's ends.
const EDGE_TOLERANCE = 8

export default class extends Controller {
  static targets = ["track", "slide", "dot", "previous", "next", "counter"]

  connect() {
    this.sync = this.sync.bind(this)
    window.addEventListener("resize", this.sync)
    this.sync()
  }

  disconnect() {
    window.removeEventListener("resize", this.sync)
  }

  next() {
    const track = this.trackTarget
    const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - EDGE_TOLERANCE
    this.scrollTrackTo(atEnd ? 0 : track.scrollLeft + track.clientWidth)
  }

  previous() {
    const track = this.trackTarget
    const atStart = track.scrollLeft <= EDGE_TOLERANCE
    this.scrollTrackTo(atStart ? track.scrollWidth : track.scrollLeft - track.clientWidth)
  }

  goTo(event) {
    const slide = this.slideTargets[Number(event.currentTarget.dataset.index)]
    if (slide) this.scrollTrackTo(slide.offsetLeft - this.trackTarget.firstElementChild.offsetLeft)
  }

  keydown(event) {
    if (event.key === "ArrowRight") {
      event.preventDefault()
      this.next()
    } else if (event.key === "ArrowLeft") {
      event.preventDefault()
      this.previous()
    }
  }

  sync() {
    const track = this.trackTarget
    const scrollable = track.scrollWidth > track.clientWidth + EDGE_TOLERANCE
    this.element.classList.toggle("is-static", !scrollable)

    const viewStart = track.scrollLeft
    const viewEnd = viewStart + track.clientWidth
    const origin = track.firstElementChild?.offsetLeft || 0

    this.slideTargets.forEach((slide, index) => {
      const center = slide.offsetLeft - origin + slide.offsetWidth / 2
      const inView = center >= viewStart && center <= viewEnd
      const dot = this.dotTargets[index]
      if (!dot) return

      dot.classList.toggle("is-active", inView)
      if (inView) {
        dot.setAttribute("aria-current", "true")
      } else {
        dot.removeAttribute("aria-current")
      }
    })

    if (this.hasCounterTarget) this.updateCounter()
  }

  // Pages are counted in slides — as many as fit across the track — since the
  // gaps between slides make the scroll width a poor measure. The last page
  // can be partly filled, so the end of the scroll range reads as the last.
  updateCounter() {
    const track = this.trackTarget
    const slides = this.slideTargets
    if (slides.length === 0) return

    const stride = slides.length > 1 ? slides[1].offsetLeft - slides[0].offsetLeft : track.clientWidth
    const perPage = Math.max(1, Math.round(track.clientWidth / stride))
    const pages = Math.ceil(slides.length / perPage)
    const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - EDGE_TOLERANCE
    const firstInView = Math.round(track.scrollLeft / stride)
    const current = atEnd ? pages : Math.min(pages, Math.floor(firstInView / perPage) + 1)
    this.counterTarget.textContent = `${current} of ${pages}`
  }

  scrollTrackTo(left) {
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    this.trackTarget.scrollTo({ left, behavior: reduceMotion ? "auto" : "smooth" })
  }
}
